/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/Table.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/Table.tsx
 * filename: Table.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * Table Component
 * Data table components for displaying tabular data
 */

import * as React from 'react';
import { clsx } from 'clsx';

const Table = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & { ref?: React.Ref<HTMLTableElement> }) => (
  <div className="relative w-full overflow-auto">
    <table ref={ref} className={clsx('w-full caption-bottom text-sm', className)} {...props} />
  </div>
);

const TableHeader = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => <thead ref={ref} className={clsx('[&_tr]:border-b', className)} {...props} />;

const TableBody = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => <tbody ref={ref} className={clsx('[&_tr:last-child]:border-0', className)} {...props} />;

const TableFooter = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <tfoot
    ref={ref}
    className={clsx(
      'border-t bg-[rgb(var(--color-bg-tertiary))]/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
);

const TableRow = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { ref?: React.Ref<HTMLTableRowElement> }) => (
  <tr
    ref={ref}
    className={clsx(
      'border-b border-[rgb(var(--color-border))] transition-colors hover:bg-[rgb(var(--color-bg-tertiary))]/50 data-[state=selected]:bg-[rgb(var(--color-bg-tertiary))]',
      className
    )}
    {...props}
  />
);

const TableHead = ({
  className,
  ref,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <th
    ref={ref}
    className={clsx(
      'h-12 px-4 text-left align-middle font-medium text-[rgb(var(--color-text-secondary))] [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
);

const TableCell = ({
  className,
  ref,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <td
    ref={ref}
    className={clsx('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
);

const TableCaption = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement> & {
  ref?: React.Ref<HTMLTableCaptionElement>;
}) => (
  <caption
    ref={ref}
    className={clsx('mt-4 text-sm text-[rgb(var(--color-text-secondary))]', className)}
    {...props}
  />
);

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
